/**
 * Frag Out Flavor — fof-v2.js
 * All functions namespaced under window.FOFv2 to prevent collisions
 * with existing theme JS, Shopify AJAX cart, Stamped.io, Klaviyo, or other apps.
 *
 * Accumulated tasks:
 *   Task A — Standard Blend PDP: FOFv2.addFavoritesToCart()
 *   Task D — Build Your Own 6-Pack: FOFv2.setBuilderDefaults()
 *   Task D/E — Preset cards: FOFv2.initPresetCards()
 */

window.FOFv2 = window.FOFv2 || {};

/**
 * FOFv2._applyingPreset
 * Set to true while setBuilderDefaults or initPresetCards is programmatically
 * dispatching change events on Simple Bundles dropdowns. Prevents the
 * manual-change handler in initPresetCards from treating these as user edits.
 */
FOFv2._applyingPreset = false;


/**
 * FOFv2.addFavoritesToCart
 * Adds a single variant to the cart via the Shopify AJAX Cart API,
 * then opens the SlideCarthq slide-out cart drawer.
 *
 * Used by:
 *   - "Get the Starter Kit" CTA on the blend PDP cross-sell section
 *   - "Get the Starter Kit Instead" CTA on the builder page cross-sell section
 *
 * Does NOT redirect to /checkout — visitors stay in the normal cart flow.
 * (Redirect to /checkout is the Starter Kit LP behavior only.)
 *
 * Post-add behavior: calls window.SLIDECART_UPDATE(callback) then SLIDECART_OPEN()
 * — confirmed from flavor-quiz.js on this store, which uses the same pattern.
 * Falls back to window.location.href = '/cart' if SLIDECART_OPEN is not available.
 *
 * @param {number} variantId — Shopify variant ID to add
 */
FOFv2.addFavoritesToCart = function(variantId) {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
  })
  .then(function(response) {
    if (!response.ok) {
      return response.json().then(function(data) {
        throw new Error(data.description || 'Add to cart failed');
      });
    }
    return response.json();
  })
  .then(function() {
    if (typeof window.SLIDECART_UPDATE === 'function') {
      window.SLIDECART_UPDATE(function() {
        window.SLIDECART_OPEN();
      });
    } else if (typeof window.SLIDECART_OPEN === 'function') {
      window.SLIDECART_OPEN();
    } else {
      window.location.href = '/cart';
    }
  })
  .catch(function(error) {
    console.error('FOFv2.addFavoritesToCart error:', error);
    window.location.href = '/cart';
  });
};


/**
 * FOFv2.setBuilderDefaults
 * Sets default selections on the Simple Bundles dropdowns to the Starter Kit blends.
 *
 * Uses polling rather than DOMContentLoaded because Simple Bundles may inject
 * dropdowns via its own AJAX/JS after page load — the selects may not exist
 * in #simple-bundles-options-container until after the app's JS has run.
 *
 * Polls every 150ms for up to 10 seconds. On first tick (150ms) the selects
 * will already be present if Simple Bundles uses the Liquid approach. For
 * AJAX injection, it will keep checking until the app finishes rendering.
 *
 * After setting each value, dispatches a 'change' event with bubbles: true.
 * This triggers Simple Bundles' own updateHiddenInputValue listener, which
 * keeps properties[_bundle_selection] in sync for correct fulfillment.
 *
 * @param {string[]} defaults — ordered array of blend names matching <option> text
 *                              values, one per dropdown in DOM order (Blend 1 first)
 */
FOFv2.setBuilderDefaults = function(defaults) {
  var maxWait = 10000;
  var interval = 150;
  var elapsed = 0;

  var check = setInterval(function() {
    elapsed += interval;

    var container = document.getElementById('simple-bundles-options-container');
    if (container) {
      var selects = container.querySelectorAll('select');
      if (selects.length >= defaults.length) {
        clearInterval(check);
        FOFv2._applyingPreset = true;
        defaults.forEach(function(preferredValue, index) {
          var select = selects[index];
          if (!select) return;

          // Try preferred Starter Kit blend first
          select.value = preferredValue;

          // If that blend is OOS (removed from options by Simple Bundles app),
          // fall back to the first available option so the slot isn't left blank
          if (!select.value) {
            var firstOption = Array.from(select.options).find(function(opt) {
              return opt.value !== '';
            });
            if (firstOption) select.value = firstOption.value;
          }

          // Trigger Simple Bundles' change listener to update
          // the hidden properties[_bundle_selection] input
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        FOFv2._applyingPreset = false;
        return;
      }
    }

    if (elapsed >= maxWait) {
      clearInterval(check);
      console.warn('FOFv2.setBuilderDefaults: timed out waiting for Simple Bundles dropdowns');
    }
  }, interval);
};


/**
 * FOFv2.initPresetCards
 * Initialises preset quick-select card behavior on combo builder pages.
 * Called via DOMContentLoaded in the fof-v2-preset-cards snippet.
 * Reads window.FOFv2PresetConfig (set inline by that snippet).
 *
 * On card click:
 *   - Fills all Simple Bundles dropdowns with the preset's blends
 *   - Uses prefix matching: 'Battle Buddy' matches 'Battle Buddy - All Purpose'
 *     so only short blend names are needed in the preset config
 *   - Falls back to first available option if a blend is OOS
 *   - Marks clicked card active, clears others
 *   - Updates the pre-selection note to the preset's note text
 *
 * On manual dropdown change (user edits after selecting a preset):
 *   - Clears the active card indicator
 *   - Updates note to "Customized from [Preset Name]. Nice choice."
 *   - Uses FOFv2._applyingPreset flag to ignore programmatic change events
 *     from card clicks and setBuilderDefaults
 */
FOFv2.initPresetCards = function() {
  var config = window.FOFv2PresetConfig;
  if (!config || !Array.isArray(config.presets)) return;

  var cards = document.querySelectorAll('.fof-preset-card');
  var noteEl = document.getElementById('fof-preselect-note');
  var activePresetIdx = 0;

  // Match a blend name against a select's options.
  // Accepts full exact match ('Battle Buddy - All Purpose') or prefix match
  // ('Battle Buddy') so presets only need short names.
  // Falls back to first non-empty option if no match (OOS handling).
  function findOptionValue(select, blendName) {
    var opts = Array.from(select.options);
    var match = opts.find(function(o) {
      return o.value === blendName || o.value.indexOf(blendName + ' - ') === 0;
    });
    if (match) return match.value;
    var fallback = opts.find(function(o) { return o.value !== ''; });
    return fallback ? fallback.value : '';
  }

  function applyPreset(idx) {
    var preset = config.presets[idx];
    var container = document.getElementById('simple-bundles-options-container');
    if (!container) return;
    var selects = container.querySelectorAll('select');
    FOFv2._applyingPreset = true;
    preset.blends.forEach(function(blendName, i) {
      if (!selects[i]) return;
      selects[i].value = findOptionValue(selects[i], blendName);
      selects[i].dispatchEvent(new Event('change', { bubbles: true }));
    });
    FOFv2._applyingPreset = false;
    activePresetIdx = idx;
    cards.forEach(function(c, i) { c.classList.toggle('is-active', i === idx); });
    if (noteEl) noteEl.textContent = preset.note;
  }

  // Card click handlers
  cards.forEach(function(card, i) {
    card.addEventListener('click', function() { applyPreset(i); });
  });

  // Manual change detection — event delegation on document.
  // Fires when the user changes a dropdown directly.
  // Skipped if a preset (or setBuilderDefaults) is programmatically updating the selects.
  document.addEventListener('change', function(e) {
    if (FOFv2._applyingPreset) return;
    var container = document.getElementById('simple-bundles-options-container');
    if (!container || !container.contains(e.target) || e.target.tagName !== 'SELECT') return;
    cards.forEach(function(c) { c.classList.remove('is-active'); });
    if (noteEl) {
      var lastPreset = config.presets[activePresetIdx];
      noteEl.textContent = 'Customized from ' + lastPreset.name + '. Nice choice.';
    }
  });
};


/**
 * FOFv2.initReviewsCarousel
 * Wires up prev/next arrow buttons on the curated reviews carousel.
 * Buttons are hidden on touch devices (≤1024px) via CSS — swipe is sufficient there.
 * Called from DOMContentLoaded in sections/fof-v2-curated-reviews.liquid.
 */
FOFv2.initReviewsCarousel = function() {
  var carousel = document.querySelector('.fof-reviews-carousel');
  if (!carousel) return;
  var track = carousel.querySelector('.fof-reviews-track');
  if (!track) return;
  function scrollByCard(dir) {
    var card = track.querySelector('.fof-review-card');
    if (!card) return;
    track.scrollBy({ left: dir * (card.offsetWidth + 16), behavior: 'smooth' });
  }
  var prevBtn = carousel.querySelector('.fof-carousel-btn--prev');
  var nextBtn = carousel.querySelector('.fof-carousel-btn--next');
  if (prevBtn) prevBtn.addEventListener('click', function() { scrollByCard(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function() { scrollByCard(1); });
};
